import React, { useEffect, useState } from "react";
import LibraryFormModal from "../components/LibraryFormModal";
import LibraryField, {
  libraryInputClass,
  librarySelectClass,
} from "../components/LibraryField";
import { ChevronDownIcon } from "../../../Icons";
import { PHARMACY_DIRECTORY_TYPES } from "../../../services/librariesCatalogService";

const EMPTY = {
  pharmacyName: "",
  pharmacyType: "Retail",
  address: "",
  city: "",
  state: "",
  zip: "",
  phone: "",
  fax: "",
  status: "Active",
};

const PharmacyFormModal = ({ isOpen, record, onClose, onSave }) => {
  const isEdit = Boolean(record?.id);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setErrors({});
    setSaving(false);
    setForm(
      record
        ? {
            pharmacyName: record.pharmacyName || "",
            pharmacyType: record.pharmacyType || "Retail",
            address: record.address || "",
            city: record.city || "",
            state: record.state || "",
            zip: record.zip || "",
            phone: record.phone || "",
            fax: record.fax || "",
            status: record.status || "Active",
          }
        : EMPTY,
    );
  }, [isOpen, record]);

  const setField = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const handleSubmit = async () => {
    if (!form.pharmacyName.trim()) {
      setErrors({ pharmacyName: "Pharmacy name is required" });
      return;
    }
    setSaving(true);
    try {
      await onSave?.({
        pharmacyName: form.pharmacyName.trim(),
        pharmacyType: form.pharmacyType.trim(),
        address: form.address.trim(),
        city: form.city.trim(),
        state: form.state.trim(),
        zip: form.zip.trim(),
        phone: form.phone.trim(),
        fax: form.fax.trim(),
        status: form.status,
      });
      onClose?.();
    } catch (err) {
      setErrors({ form: err.message || "Save failed" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <LibraryFormModal
      isOpen={isOpen}
      title={isEdit ? "Edit Pharmacy" : "Create New Pharmacy"}
      onClose={onClose}
      onSubmit={handleSubmit}
      isSaving={saving}
      submitLabel={isEdit ? "Update" : "Create"}
    >
      {errors.form ? (
        <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2">
          {errors.form}
        </p>
      ) : null}
      <LibraryField
        id="pharm-name"
        label="Pharmacy Name"
        required
        error={errors.pharmacyName}
      >
        <input
          id="pharm-name"
          className={libraryInputClass(errors.pharmacyName)}
          value={form.pharmacyName}
          onChange={(e) => setField("pharmacyName", e.target.value)}
          maxLength={255}
          autoFocus
        />
      </LibraryField>
      <LibraryField id="pharm-type" label="Pharmacy Type">
        <div className="relative">
          <select
            id="pharm-type"
            className={librarySelectClass()}
            value={form.pharmacyType}
            onChange={(e) => setField("pharmacyType", e.target.value)}
          >
            {PHARMACY_DIRECTORY_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          <ChevronDownIcon
            size={14}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
          />
        </div>
      </LibraryField>
      <LibraryField id="pharm-address" label="Address">
        <input
          id="pharm-address"
          className={libraryInputClass()}
          value={form.address}
          onChange={(e) => setField("address", e.target.value)}
          maxLength={500}
        />
      </LibraryField>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <LibraryField id="pharm-city" label="City">
          <input
            id="pharm-city"
            className={libraryInputClass()}
            value={form.city}
            onChange={(e) => setField("city", e.target.value)}
          />
        </LibraryField>
        <LibraryField id="pharm-state" label="State">
          <input
            id="pharm-state"
            className={libraryInputClass()}
            value={form.state}
            onChange={(e) => setField("state", e.target.value)}
          />
        </LibraryField>
        <LibraryField id="pharm-zip" label="Zip">
          <input
            id="pharm-zip"
            className={libraryInputClass()}
            value={form.zip}
            onChange={(e) => setField("zip", e.target.value)}
          />
        </LibraryField>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <LibraryField id="pharm-phone" label="Phone">
          <input
            id="pharm-phone"
            className={libraryInputClass()}
            value={form.phone}
            onChange={(e) => setField("phone", e.target.value)}
          />
        </LibraryField>
        <LibraryField id="pharm-fax" label="Fax">
          <input
            id="pharm-fax"
            className={libraryInputClass()}
            value={form.fax}
            onChange={(e) => setField("fax", e.target.value)}
          />
        </LibraryField>
      </div>
      <LibraryField id="pharm-status" label="Status">
        <div className="relative">
          <select
            id="pharm-status"
            className={librarySelectClass()}
            value={form.status}
            onChange={(e) => setField("status", e.target.value)}
          >
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
          <ChevronDownIcon
            size={14}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
          />
        </div>
      </LibraryField>
    </LibraryFormModal>
  );
};

export default PharmacyFormModal;
